import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Alert
} from '../../styles/components';
import { SecretDisplayModal } from '../common/SecretDisplayModal';
import {
  useGetManagementClientQuery,
  useUpdateManagementClientMutation,
  useCycleClientSecretMutation,
  type ManagementClient
} from '../../store/api/managementClientsApi';
import { useNotifications } from '../../hooks/useNotifications';
import { usePermissions } from '../../hooks/usePermissions';
import {
  XMarkIcon,
  KeyIcon,
  ServerIcon,
  PlusIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

interface ManagementClientEditModalProps {
  clientId: string;
  onClose: () => void;
}

const EditSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borders.radius.lg};
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const CollapsibleHeader = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.$isExpanded ? props.theme.colors.gray[50] : 'white'};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borders.radius.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.gray[50]};
  }
`;

const CollapsibleTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  h3 {
    margin: 0;
    font-size: ${props => props.theme.fontSizes.base};
    font-weight: ${props => props.theme.fontWeights.semibold};
    color: ${props => props.theme.colors.gray[900]};
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${props => props.theme.colors.gray[500]};
  }
`;

const ChevronIcon = styled.div`
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${props => props.theme.colors.gray[400]};
  }
`;

const CollapsibleContent = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-top: none;
  border-bottom-left-radius: ${props => props.theme.borders.radius.md};
  border-bottom-right-radius: ${props => props.theme.borders.radius.md};
  background: white;
`;

const StatusBadge = styled.span<{ $variant: 'active' | 'disabled' }>`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.sm};
  background-color: ${props => props.$variant === 'active'
    ? props.theme.colors.success[50]
    : props.theme.colors.gray[100]};
  color: ${props => props.$variant === 'active'
    ? props.theme.colors.success[600]
    : props.theme.colors.gray[600]};
  border: 1px solid ${props => props.$variant === 'active'
    ? props.theme.colors.success[200]
    : props.theme.colors.gray[300]};
`;

const IPListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const IPItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.gray[50]};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-family: monospace;
  font-size: ${props => props.theme.fontSizes.sm};
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xs};
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.error[500]};
  transition: color 0.2s ease;

  &:hover:not(:disabled) {
    color: ${props => props.theme.colors.error[700]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const AddIPContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ErrorText = styled.div`
  color: ${props => props.theme.colors.error[600]};
  font-size: ${props => props.theme.fontSizes.sm};
  margin-top: ${props => props.theme.spacing.xs};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const FieldLabel = styled(Label)`
  margin-bottom: ${props => props.theme.spacing.xs};
`;

export function ManagementClientEditModal({ clientId, onClose }: ManagementClientEditModalProps) {
  const [generatedSecret, setGeneratedSecret] = useState<string>('');
  const [showSecretModal, setShowSecretModal] = useState(false);

  // IP Whitelist state
  const [isIPSectionExpanded, setIsIPSectionExpanded] = useState(false);
  const [ipAddresses, setIpAddresses] = useState<string[]>([]);
  const [newIPAddress, setNewIPAddress] = useState('');
  const [ipError, setIpError] = useState<string>('');

  const { notify } = useNotifications();
  const { isSupport } = usePermissions();

  const { data: client, isLoading: clientLoading, refetch: refetchClient } = useGetManagementClientQuery(clientId);
  const [updateClient, { isLoading: isUpdating }] = useUpdateManagementClientMutation();
  const [cycleSecret, { isLoading: isCyclingSecret }] = useCycleClientSecretMutation();

  const isLoading = isUpdating || isCyclingSecret;

  // Initialize IP addresses when client data loads
  React.useEffect(() => {
    console.log('Client data loaded:', client);
    if (client && client.clientIPs) {
      setIpAddresses(client.clientIPs);
    }
  }, [client]);

  const handleCycleSecret = async () => {
    try {
      const result = await cycleSecret({ clientId }).unwrap();
      setGeneratedSecret(result.secret);
      setShowSecretModal(true);
    } catch (error: any) {
      notify.error('Failed to cycle secret', error?.data?.errors?.[0] || 'Please try again.');
    }
  };

  const handleSecretComplete = () => {
    setShowSecretModal(false);
    setGeneratedSecret('');
    refetchClient();
  };

  const validateIPv4OrCIDR = (input: string): boolean => {
    // Check if it's CIDR notation
    if (input.includes('/')) {
      const [ip, mask] = input.split('/');
      const ipPattern = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      const maskNum = parseInt(mask, 10);
      return ipPattern.test(ip) && !isNaN(maskNum) && maskNum >= 0 && maskNum <= 32;
    }
    // Individual IP
    const ipPattern = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipPattern.test(input);
  };

  const handleAddIP = async () => {
    if (!client) return;

    const trimmedIP = newIPAddress.trim();

    if (!trimmedIP) {
      setIpError('Please enter an IP address or CIDR range');
      return;
    }

    if (!validateIPv4OrCIDR(trimmedIP)) {
      setIpError('Invalid format. Examples: 192.168.1.1 or 192.168.1.0/24');
      return;
    }

    if (ipAddresses.includes(trimmedIP)) {
      setIpError('This IP address is already in the whitelist');
      return;
    }

    const updatedIPs = [...ipAddresses, trimmedIP];

    const payload = {
      clientId: client.clientId,
      clientIPs: updatedIPs
    };
    console.log('UpdateClient payload:', payload);

    try {
      await updateClient(payload).unwrap();

      setIpAddresses(updatedIPs);
      setNewIPAddress('');
      setIpError('');
      notify.success('IP address added', 'IP address has been added to the whitelist.');
      refetchClient();
    } catch (error: any) {
      notify.error('Failed to add IP address', error?.data?.errors?.[0] || 'Please try again.');
    }
  };

  const handleRemoveIP = async (ipToRemove: string) => {
    if (!client) return;

    const updatedIPs = ipAddresses.filter(ip => ip !== ipToRemove);

    try {
      await updateClient({
        clientId: client.clientId,
        clientIPs: updatedIPs
      }).unwrap();

      setIpAddresses(updatedIPs);
      notify.success('IP address removed', 'IP address has been removed from the whitelist.');
      refetchClient();
    } catch (error: any) {
      notify.error('Failed to remove IP address', error?.data?.errors?.[0] || 'Please try again.');
    }
  };

  if (clientLoading || !client) {
    return (
      <Modal $isOpen>
        <ModalOverlay onClick={onClose} />
        <ModalContent>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            Loading client details...
          </div>
        </ModalContent>
      </Modal>
    );
  }

  // Show secret modal instead of edit modal when secret is being displayed
  if (showSecretModal) {
    return (
      <SecretDisplayModal
        isOpen={showSecretModal}
        secret={generatedSecret}
        title="New Client Secret Generated"
        clientId={client?.clientId || ''}
        secretType="client-secret"
        onComplete={handleSecretComplete}
      />
    );
  }

  return (
    <Modal $isOpen>
      <ModalOverlay onClick={onClose} />
      <ModalContent style={{ maxWidth: '800px' }}>
        <ModalHeader>
          <ModalTitle>Edit Management Client - {client.clientId}</ModalTitle>
          <Button variant="outline" size="sm" onClick={onClose}>
            <XMarkIcon style={{ width: '1rem', height: '1rem' }} />
          </Button>
        </ModalHeader>

        <ModalBody>
          {/* IP Whitelist Section - for support users only */}
          {isSupport && (
            <div style={{ marginBottom: '1.5rem' }}>
              <CollapsibleHeader
                $isExpanded={isIPSectionExpanded}
                onClick={() => setIsIPSectionExpanded(!isIPSectionExpanded)}
              >
                <CollapsibleTitle>
                  <ServerIcon />
                  <h3>IP Whitelisting</h3>
                </CollapsibleTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <StatusBadge $variant={ipAddresses.length === 0 ? 'disabled' : 'active'}>
                    {ipAddresses.length === 0
                      ? 'Disabled'
                      : `${ipAddresses.length} IP${ipAddresses.length === 1 ? '' : 's'} whitelisted`}
                  </StatusBadge>
                  <ChevronIcon>
                    {isIPSectionExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  </ChevronIcon>
                </div>
              </CollapsibleHeader>

              {isIPSectionExpanded && (
                <CollapsibleContent>
                  {ipAddresses.length === 0 ? (
                    <EmptyState>
                      No IP restrictions configured. All IP addresses are allowed.
                    </EmptyState>
                  ) : (
                    <IPListContainer>
                      {ipAddresses.map((ip, index) => (
                        <IPItem key={index}>
                          <span>{ip}</span>
                          <RemoveButton
                            onClick={() => handleRemoveIP(ip)}
                            disabled={isLoading}
                            title="Remove IP address"
                          >
                            <XCircleIcon />
                          </RemoveButton>
                        </IPItem>
                      ))}
                    </IPListContainer>
                  )}

                  <div>
                    <FieldLabel htmlFor="newIPAddress">Add IP Address</FieldLabel>
                    <AddIPContainer>
                      <div style={{ flex: 1 }}>
                        <Input
                          id="newIPAddress"
                          value={newIPAddress}
                          onChange={(e) => {
                            setNewIPAddress(e.target.value);
                            setIpError('');
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddIP();
                            }
                          }}
                          placeholder="Enter IP or CIDR range (e.g., 192.168.1.1 or 192.168.1.0/24)"
                          disabled={isLoading}
                        />
                      </div>
                      <Button
                        onClick={handleAddIP}
                        disabled={isLoading || !newIPAddress.trim()}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          minWidth: 'auto'
                        }}
                      >
                        <PlusIcon style={{ width: '1rem', height: '1rem' }} />
                        Add
                      </Button>
                    </AddIPContainer>
                    {ipError && <ErrorText>{ipError}</ErrorText>}
                  </div>

                  <Alert variant="info" style={{ marginTop: '1rem' }}>
                    When IP whitelisting is enabled (one or more IPs configured), only requests from these IP addresses will be allowed.
                    Remove all IPs to disable whitelisting and allow all addresses.
                  </Alert>
                </CollapsibleContent>
              )}
            </div>
          )}

          {/* Security Settings - for support users only */}
          {isSupport && (
            <EditSection>
              <SectionTitle>
                <KeyIcon />
                Security Settings
              </SectionTitle>

              <div style={{ marginBottom: '1rem' }}>
                <FieldLabel>Client Secret</FieldLabel>
                <div style={{
                  padding: '0.5rem',
                  background: '#f9fafb',
                  borderRadius: '0.375rem'
                }}>
                  ••••••••••••••••••••••••••••••••
                </div>
              </div>

              <Alert variant="warning" style={{ marginBottom: '1rem' }}>
                Cycling the client secret will generate a new secret and invalidate the current one.
                Make sure to update any applications using this client.
              </Alert>

              <Button
                variant="outline"
                onClick={handleCycleSecret}
                disabled={isLoading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <KeyIcon style={{ width: '1rem', height: '1rem' }} />
                {isCyclingSecret ? 'Cycling Secret...' : 'Cycle Client Secret'}
              </Button>
            </EditSection>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
